import { useMemo } from 'react';
import type { CSSProperties } from 'react';
import { theme, type MenuProps } from 'antd';
import { AppButton, AppDropdown } from '@/components/atoms';
import type { PatientFormData } from '@pages/main/patient/types';
import {
  getPatientInformationHistoryDropdownParts,
  healthInsuranceTimelineMinQualMaxEndYmd,
  PATIENT_HISTORY_FROM_DATE_COLUMN_CH,
  sortPatientInformationsForHistoryDropdown,
} from '@pages/main/patient/utils/patientHistoryHelpers';
import { historyDropdownPopupStyle } from './PatientSectionShared.helpers';

interface PatientInfoHistoryMenuProps {
  isEditMode: boolean;
  isCurrent: boolean;
  disabled: boolean;
  patientData?: PatientFormData | null;
  patientInformationRowId: unknown;
  patientInformationHistoryDraftByUrl: boolean;
  onHistorySwitch?: (type: 'patient_information', historyId: number) => void;
  onHistoryDelete?: (type: 'patient_information', historyId: number) => void;
  onPatientHistoryCreateCancel?: () => void | Promise<void>;
}

export function usePatientInfoHistoryMenu({
  isEditMode,
  isCurrent,
  disabled,
  patientData,
  patientInformationRowId,
  patientInformationHistoryDraftByUrl,
  onHistorySwitch,
  onHistoryDelete,
  onPatientHistoryCreateCancel,
}: PatientInfoHistoryMenuProps) {
  const { token } = theme.useToken();

  const historyCreateCancelButtonStyle = disabled
    ? undefined
    : {
        '--patient-history-cancel-bg': token.colorWarning,
        '--patient-history-cancel-bg-hover': token.colorWarningHover,
        '--patient-history-cancel-border': token.colorWarning,
        '--patient-history-cancel-border-hover': token.colorWarningHover,
        '--patient-history-cancel-text': token.colorTextLightSolid,
      } as CSSProperties;

  const sortedPatientInfos = useMemo(
    () =>
      patientData?.patient_informations?.length
        ? sortPatientInformationsForHistoryDropdown(patientData.patient_informations)
        : [],
    [patientData?.patient_informations]
  );

  const oldestHealthInsuranceQualificationDate = useMemo(
    () =>
      patientData?.official
        ? healthInsuranceTimelineMinQualMaxEndYmd(patientData.health_insurance_cards)?.minQ
        : undefined,
    [patientData?.official, patientData?.health_insurance_cards]
  );

  const historyRecordMenuItems = useMemo(
    () =>
      sortedPatientInfos.map((info, index) => {
        const older = sortedPatientInfos[index + 1];
        const parts = getPatientInformationHistoryDropdownParts(
          info,
          older,
          oldestHealthInsuranceQualificationDate
        );
        const fromColStyle = { width: `${PATIENT_HISTORY_FROM_DATE_COLUMN_CH}ch` } as const;
        return {
          key: info.id?.toString() || `info-${index}`,
          // Legacy: fixed-width FROM column; nowrap + wide menu so dates are not broken at hyphens.
          label: (
            <span className="inline-flex max-w-none items-start gap-2 whitespace-nowrap">
              <i
                className={
                  info.is_current
                    ? 'fa fa-dot-circle-o mt-0.5 shrink-0 text-blue-600'
                    : 'fa fa-circle-o mt-0.5 shrink-0 text-gray-500'
                }
                aria-hidden="true"
              />
              <span className="inline-flex shrink-0 flex-nowrap items-baseline gap-x-0 text-sm">
                <span
                  className="inline-block shrink-0 whitespace-nowrap pr-1 text-left font-mono tabular-nums tracking-tight"
                  style={fromColStyle}
                >
                  {parts.fromYmd}
                </span>
                <span className="shrink-0 whitespace-nowrap">から</span>
                <span
                  className="inline-block shrink-0 whitespace-nowrap text-left font-mono tabular-nums tracking-tight"
                  style={fromColStyle}
                >
                  {parts.toYmd}
                </span>
                {parts.address ? (
                  <>
                    <span className="shrink-0">:</span>
                    <span className="shrink-0 whitespace-nowrap">{parts.address}</span>
                  </>
                ) : null}
              </span>
            </span>
          ),
          onClick: () => onHistorySwitch?.('patient_information', info.id!),
        };
      }),
    [oldestHealthInsuranceQualificationDate, sortedPatientInfos, onHistorySwitch]
  );

  const historyDropdownItems = useMemo((): MenuProps['items'] => {
    const newHistoryLabel =
      isEditMode && !isCurrent ? '新しい履歴を入力する' : '新しい情報を入力する';
    const items: MenuProps['items'] = [
      {
        key: 'new-history',
        label: newHistoryLabel,
        className: 'text-blue-600',
        onClick: () => onHistorySwitch?.('patient_information', 0),
      },
    ];
    // A2 edit.blade: divider + list only when count() > 1; single row -> 新しい情報のみ.
    if (historyRecordMenuItems.length > 1) {
      items.push({ type: 'divider', key: 'divider-patient-history' });
      items.push(...historyRecordMenuItems);
    }
    return items;
  }, [historyRecordMenuItems, isCurrent, isEditMode, onHistorySwitch]);

  const hasPersistedPatientInformationRowId =
    typeof patientInformationRowId === 'number' && Number.isFinite(patientInformationRowId);
  const showPatientHistoryCreateCancel =
    isEditMode &&
    !!patientData?.id &&
    (patientData?.patient_informations?.length ?? 0) > 0 &&
    (!hasPersistedPatientInformationRowId || patientInformationHistoryDraftByUrl);

  const historyMenu = isEditMode ? (
    <div className="flex gap-2">
      {showPatientHistoryCreateCancel && onPatientHistoryCreateCancel ? (
        <AppButton
          type="default"
          size="small"
          className="!border-[var(--patient-history-cancel-border)] !bg-[var(--patient-history-cancel-bg)] !text-[var(--patient-history-cancel-text)] hover:!border-[var(--patient-history-cancel-border-hover)] hover:!bg-[var(--patient-history-cancel-bg-hover)]"
          style={historyCreateCancelButtonStyle}
          disabled={disabled}
          onClick={() => void onPatientHistoryCreateCancel()}
        >
          履歴作成取消
        </AppButton>
      ) : (
        <>
          {/*
           * A2 edit.blade: delete_history float-left, 履歴 float-right — DOM order: 履歴削除 then 履歴.
           */}
          {onHistoryDelete &&
            !!patientData?.id &&
            (patientData?.patient_informations?.length ?? 0) > 1 &&
            hasPersistedPatientInformationRowId && (
              <AppButton
                type="default"
                size="small"
                danger
                disabled={disabled}
                onClick={() =>
                  onHistoryDelete('patient_information', patientInformationRowId as number)
                }
                className={disabled ? '!bg-white/20 !text-white/40 !border-white/10' : ''}
              >
                履歴削除
              </AppButton>
            )}
          <AppDropdown
            menu={{
              items: historyDropdownItems,
              style: historyDropdownPopupStyle,
            }}
            styles={{ root: historyDropdownPopupStyle }}
            trigger={['click']}
            placement="bottomLeft"
            disabled={disabled}
          >
            <AppButton
              type="default"
              size="small"
              disabled={disabled}
              className={disabled ? '!bg-white/20 !text-white/60 !border-white/20' : ''}
            >
              履歴 <i className="fa fa-angle-down" aria-hidden="true" />
            </AppButton>
          </AppDropdown>
        </>
      )}
    </div>
  ) : null;

  return {
    historyMenu,
    showPatientHistoryCreateCancel,
  };
}
